import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import { Box, Container, Typography, Grid, TextField, Button, Alert, CircularProgress } from "@mui/material";
import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { colors } from "components/Theme/WebTheme.js";
import { FONT_FAMILY } from "../common/sharedFunctions.js";
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';

const dashboardRoutes = [];

const useStyles = makeStyles((theme) => ({
  heroSection: {
    background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 100%)',
    padding: '120px 0 80px',
    marginTop: '80px',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 100%)`,
      pointerEvents: 'none',
      zIndex: 1
    },
    '& > *': {
      position: 'relative',
      zIndex: 2
    }
  },
  contactCard: {
    background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '16px',
    padding: '32px',
    height: '100%',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      border: '1px solid rgba(212, 175, 55, 0.5)',
      boxShadow: '0 12px 40px rgba(212, 175, 55, 0.2)'
    }
  },
  formSection: {
    background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '16px',
    padding: '40px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      color: '#ffffff',
      fontFamily: FONT_FAMILY,
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: colors.Button_Primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.Button_Primary,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      fontFamily: FONT_FAMILY,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: colors.Button_Primary,
    },
  },
  submitButton: {
    background: `linear-gradient(45deg, ${colors.Button_Primary} 0%, #f0c040 100%)`,
    color: '#000000',
    fontWeight: 'bold',
    padding: '12px 40px',
    borderRadius: '8px',
    fontSize: '16px',
    textTransform: 'none',
    fontFamily: FONT_FAMILY,
    transition: 'all 0.3s ease',
    '&:hover': {
      background: `linear-gradient(45deg, #f0c040 0%, ${colors.Button_Primary} 100%)`,
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)',
    },
  },
  iconBox: {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    background: `linear-gradient(135deg, ${colors.Button_Primary} 0%, #f0c040 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
}));

export default function ContactUs(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { ...rest } = props;
  const settings = useSelector(state => state.settingsdata.settings);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/mldqlwkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: t('contact_success') || 'Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.'
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: t('contact_error') || 'Une erreur est survenue. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: '-8px', background: '#0a0a0a' }}>
      <Header
        color="black"
        routes={dashboardRoutes}
        rightLinks={<HeaderLinks />}
        fixed
        {...rest}
      />

      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 'bold',
              color: '#ffffff',
              mb: 2,
              fontSize: { xs: '32px', md: '48px' }
            }}
          >
            {t('contact_us')}
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{
              fontFamily: FONT_FAMILY,
              color: colors.Button_Primary,
              mb: 2
            }}
          >
            Nous sommes à votre écoute
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              fontFamily: FONT_FAMILY,
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '700px',
              margin: '0 auto'
            }}
          >
            Une question, une demande de devis ou besoin d'informations ? Notre équipe est là pour vous répondre rapidement.
          </Typography>
        </Container>
      </Box>

      {/* Contact Info Cards */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Box className={classes.contactCard}>
              <Box className={classes.iconBox}>
                <EmailIcon sx={{ fontSize: 32, color: '#000000' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 'bold',
                  color: '#ffffff',
                  mb: 1
                }}
              >
                Email
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  color: 'rgba(255, 255, 255, 0.7)',
                  wordBreak: 'break-word'
                }}
              >
                {settings.contact_email || 'contact@allodriver.com'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className={classes.contactCard}>
              <Box className={classes.iconBox}>
                <PhoneIcon sx={{ fontSize: 32, color: '#000000' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 'bold',
                  color: '#ffffff',
                  mb: 1
                }}
              >
                {t('phone')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  color: 'rgba(255, 255, 255, 0.7)'
                }}
              >
                {settings.CompanyPhone || '+33 X XX XX XX XX'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className={classes.contactCard}>
              <Box className={classes.iconBox}>
                <LocationOnIcon sx={{ fontSize: 32, color: '#000000' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 'bold',
                  color: '#ffffff',
                  mb: 1
                }}
              >
                Adresse
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  color: 'rgba(255, 255, 255, 0.7)'
                }}
              >
                {settings.CompanyAddress || 'Montauban, France'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Contact Form */}
        <Box className={classes.formSection}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 'bold',
              color: '#ffffff',
              mb: 3,
              textAlign: 'center'
            }}
          >
            Envoyez-nous un message
          </Typography>

          {status.message && (
            <Alert 
              severity={status.type} 
              sx={{ mb: 3, fontFamily: FONT_FAMILY }}
              onClose={() => setStatus({ type: '', message: '' })}
            >
              {status.message}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Honeypot anti-spam field */}
            <input
              type="text"
              name="_gotcha"
              style={{ display: 'none' }}
              tabIndex="-1"
              autoComplete="off"
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  name="name"
                  label="Nom complet"
                  value={formData.name}
                  onChange={handleChange}
                  className={classes.textField}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  name="email"
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleChange}
                  className={classes.textField}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  name="phone"
                  type="tel"
                  label="Téléphone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={classes.textField}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  name="subject"
                  label="Sujet"
                  value={formData.subject}
                  onChange={handleChange}
                  className={classes.textField}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  name="message"
                  label="Message"
                  multiline
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className={classes.textField}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  className={classes.submitButton}
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Container>

      <Footer />
    </div>
  );
}
